import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_RowSelectionState,
  useMaterialReactTable,
} from 'material-react-table';
import { FC, useMemo, useState } from 'react';
import ConfirmImportDevicesModal from '@/components/modals/confirm-import-devices';
import { HeadingGroup } from '@/components/ui/heading-group';
import { StatusBadge } from '@/components/ui/status-badge';
import { strings } from '@/constants/strings';
import { HeadingGroupContainerLayout } from '@/layouts/heading-group-container-layout';

import { useGetFloatingDevicesQuery } from '@/redux/api/endpoints/device-import';
import { composeTableOptions } from '@/table';
import { AlexaDevice } from '@/types/device';
import { SelectedAlexaDevicesActionBar } from './selected-alexadevices-action-bar';

const columns: MRT_ColumnDef<AlexaDevice>[] = [
  {
    accessorKey: 'serialNumber',
    header: 'Device Serial Number (DSN)',
    size: 150,
  },
  {
    accessorKey: 'model',
    header: 'Model',
    filterVariant: 'multi-select',
    size: 150,
  },
  {
    id: 'status',
    accessorFn: ({ connectivity }) => connectivity?.status,
    header: 'Status',
    grow: false,
    filterVariant: 'multi-select',
    size: 100,
    Cell: ({ row }) => {
      const connectivityStatus = row.original.connectivity?.status ?? 'Unknown';
      return (
        <>
          {'verifiedASP' in row.original &&
          row.original.verifiedASP === 'error' ? (
            <StatusBadge text="Error" variant="warning" />
          ) : (
            <StatusBadge
              text={connectivityStatus}
              variant={connectivityStatus === 'Online' ? 'success' : 'alert'}
            />
          )}
        </>
      );
    },
  },
];

/**
 * This page is used to import devices from ASP into the Platform.
 */
export const ImportingPage: FC = () => {
  const { data: devices = [], isLoading, isFetching } = useGetFloatingDevicesQuery(); // prettier-ignore

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});

  const selectedDevices = useMemo<AlexaDevice[]>(
    () =>
      (Object.keys(rowSelection) as unknown[] as number[]).map(
        (rowIndex) => devices[rowIndex],
      ),
    [rowSelection, devices],
  );

  const handleComplete = () => {
    setIsModalOpen(false);
    setRowSelection({});
  };

  const table = useMaterialReactTable({
    ...composeTableOptions<AlexaDevice>({
      enableRowActions: true,
      enableRowSelection: true,
    }),
    columns,
    enableColumnFilters: true,
    enableFacetedValues: true,
    data: devices,
    state: {
      isLoading,
      showColumnFilters: true,
      rowSelection,
      showProgressBars: isFetching,
      ...(isLoading
        ? {
            pagination: {
              pageIndex: 0,
              pageSize: 1,
            },
          }
        : {}),
    },
    onRowSelectionChange: setRowSelection,
  });

  return (
    <>
      <HeadingGroupContainerLayout>
        <HeadingGroup
          subtitlePrefix={strings.importing.subtitle}
          title={strings.importing.title}
        />
      </HeadingGroupContainerLayout>
      <SelectedAlexaDevicesActionBar
        linkDeviceButtonText={`Import device${selectedDevices.length > 1 ? 's' : ''}`}
        onLinkDevicesClick={() => setIsModalOpen(true)}
        selectedDevices={selectedDevices}
      />
      <MaterialReactTable table={table} />

      <ConfirmImportDevicesModal
        isOpen={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onComplete={handleComplete}
        selectedDevices={selectedDevices}
      />
    </>
  );
};
